import { createStore, useStore } from "@/lib/store";
import type { MomentSpec } from "./types";

// The moment queue: moments play one at a time, in the order they were asked for, so a reveal, the
// level-up it caused and the quests it completed never overlap.

export interface QueuedMoment {
  id: number;
  spec: MomentSpec;
  resolve: () => void;
}

interface QueueState {
  current: QueuedMoment | null;
  waiting: QueuedMoment[];
}

export const momentQueue = createStore<QueueState>({ current: null, waiting: [] });
let nextId = 1;

/** Plays a moment after the ones already queued. Resolves when it has closed (played or skipped). */
export function play(spec: MomentSpec): Promise<void> {
  return new Promise((resolve) => {
    const item: QueuedMoment = { id: nextId++, spec, resolve };
    momentQueue.set((state) =>
      state.current
        ? { ...state, waiting: [...state.waiting, item] }
        : { current: item, waiting: state.waiting },
    );
  });
}

/** Called by the host when the current moment closes. */
export function finish(id: number) {
  const { current } = momentQueue.get();
  if (current?.id !== id) return;
  current.resolve();
  current.spec.onDone?.();
  momentQueue.set((state) => {
    const [next = null, ...rest] = state.waiting;
    return { current: next, waiting: rest };
  });
}

/** The chain of the moment playing now, if any: a celebration of the same chain must not play a second
 * sting (sequencer.ts). */
export function playingChain(): string | undefined {
  return momentQueue.get().current?.spec.chain;
}

/** Drops every moment, playing or waiting (e.g. on sign-out). */
export function clearMoments() {
  const { current, waiting } = momentQueue.get();
  [current, ...waiting].forEach((item) => item?.resolve());
  momentQueue.set({ current: null, waiting: [] });
}

export const useMomentQueue = () => useStore(momentQueue);
